import { spacings } from "themes/variables"
import React, { useMemo, useCallback } from "react"
import { Flex, Button } from "antd"
import { Title } from "ui/Typography"
import WarningOutlinedWithBg from "ui/WarningOutlinedWithBg"
import styles from "./index.module.css"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { routes } from "router/routes"
import {
  ACCOUNT_STATUS_CONFIRMATION,
  ACCOUNT_STATUS_WARNING,
  CANCEL_DELETION
} from "pagesV2/pro/Settings/ProSettings/AccountManagement/constants"
import { useSingleModal } from "pagesV2/pro/Settings/ProSettings/AccountManagement/hooks/useSingleModal"
import AccountStatusPrompt from "pagesV2/pro/Settings/ProSettings/AccountManagement/modals/AccountStatusPrompt"
import ConfirmAccountDeletionModal from "pagesV2/pro/Settings/ProSettings/AccountManagement/modals/ConfirmAccountDeletion"
import { useDispatch } from "react-redux"
import { accountActions } from "pagesV2/pro/Settings/ProSettings/AccountManagement/actions"
import PropTypes from "prop-types"
import moment from "moment"

const AccountNoticeBanner = ({ user }) => {
  const { t } = useTranslation()
  const { open, propsFor, isOpen, close } = useSingleModal()
  const dispatch = useDispatch()
  const rawScheduledDeletionDate = user?.scheduled_deletion_date
  const hasScheduledDeletion = Boolean(rawScheduledDeletionDate)
  const scheduledDeletionDate = useMemo(
    () => (hasScheduledDeletion ? moment(rawScheduledDeletionDate).format("MMMM D, YYYY") : ""),
    [hasScheduledDeletion, rawScheduledDeletionDate]
  )

  const isDeactivated = useMemo(() => user?.account_status === "deactivated", [user?.account_status])

  const handleActivateAccount = useCallback(() => {
    if (isDeactivated && !hasScheduledDeletion) {
      open(ACCOUNT_STATUS_WARNING)
    } else {
      open(CANCEL_DELETION)
    }
  }, [isDeactivated, hasScheduledDeletion, open])

  const proceedToAccountStatusConfirmation = useCallback(() => {
    open(ACCOUNT_STATUS_CONFIRMATION)
  }, [open])

  const handleConfirmByUser = useCallback(
    async ({ password }) => {
      const isDeactivateAction = isOpen(ACCOUNT_STATUS_CONFIRMATION)
      await accountActions.handleConfirmByUser({
        dispatch,
        isDeactivateAction,
        password,
        isDeactivated,
        scheduledDeletionDate: rawScheduledDeletionDate
      })
      close()
    },
    [dispatch, isOpen, isDeactivated, rawScheduledDeletionDate, close]
  )

  const bannerText = useMemo(() => {
    if (isDeactivated && !hasScheduledDeletion) return t("settings.deactivate_profile.banner_title")
    if (hasScheduledDeletion)
      return t("settings.scheduled_deletion_notice_title", {
        scheduledDeletionDate
      })
    return ""
  }, [isDeactivated, hasScheduledDeletion, scheduledDeletionDate, t])

  if (!isDeactivated && !hasScheduledDeletion) {
    return null
  }
  return (
    <>
      <Flex gap={spacings.S} justify="space-between" className={`py-s px-4xl ${styles.noticeBannerContainer}`} align="center">
        <WarningOutlinedWithBg />
        <Title level={4}>
          {bannerText}
          <Link to={routes.proSettingsAccountManagement()}> {t("global.see_details")} </Link>
        </Title>
        <Button color="primary" variant="solid" size="large" onClick={handleActivateAccount}>
          {isDeactivated && !hasScheduledDeletion ? t("settings.deactivate_profile.activate_account") : t("settings.cancel_deletion")}
        </Button>
      </Flex>
      {isOpen(ACCOUNT_STATUS_WARNING) && (
        <AccountStatusPrompt
          text={t("settings.activate_account_description")}
          title={t("settings.activate_account")}
          primaryLabel={t("settings.activate_account")}
          onProceed={proceedToAccountStatusConfirmation}
          primaryColor="primary"
          {...propsFor(ACCOUNT_STATUS_WARNING)}
        />
      )}
      {isOpen(ACCOUNT_STATUS_CONFIRMATION) && (
        <ConfirmAccountDeletionModal
          primaryLabel={t("settings.confirm_activate")}
          primaryColor="primary"
          title={t("settings.confirm_activate")}
          text={t("settings.confirm_activate_account_description")}
          {...propsFor(ACCOUNT_STATUS_CONFIRMATION)}
          onConfirm={handleConfirmByUser}
        />
      )}
      {isOpen(CANCEL_DELETION) && (
        <ConfirmAccountDeletionModal
          primaryLabel={t("settings.cancel_deletion")}
          primaryColor="primary"
          title={t("settings.cancel_deletion_title")}
          text={t("settings.cancel_deletion_description", { scheduledDeletionDate })}
          {...propsFor(CANCEL_DELETION)}
          onConfirm={handleConfirmByUser}
        />
      )}
    </>
  )
}

AccountNoticeBanner.propTypes = {
  user: {
    account_status: PropTypes.string
  }
}

export default AccountNoticeBanner
